import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import LoadingOverlay from "../../components/LoadingOverlay/LoadingOverlay";
import ProfileInfo from "../../components/ProfileInfo/ProfileInfo";
import ProfileFilter from "../../components/ProfileFilter/ProfileFilter";
import Seo from "../../components/Seo/Seo";
import { ROUTES, buildPlayerPath, isOwnPlayerPath } from "../../utils/appRoutes";
import { formatProfileDisplayName } from "../../utils/profileDisplayName";
import "../Profile/Profile.css";

function PublicProfile({ isAuthenticated = false, currentUser = null }) {
  const { playerId } = useParams();
  const location = useLocation();
  const [userData, setUserData] = useState(null);
  const [userGames, setUserGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const API_URL = process.env.REACT_APP_API_URL;

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const tabParam = searchParams.get("tab");
  const initialTab = ["ranking", "games"].includes(tabParam) ? tabParam : "ranking";
  const canonicalPath = buildPlayerPath(playerId);
  const playerName = formatProfileDisplayName(userData, { readOnly: true });
  const viewingOwnProfile = isAuthenticated && isOwnPlayerPath(playerId, currentUser?._id);

  useEffect(() => {
    let isActive = true;

    const loadPublicProfile = async () => {
      if (!playerId) {
        setError("Player not found.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const profileUrl = `${API_URL}/profile/public/${encodeURIComponent(playerId)}`;
        const gamesUrl = `${profileUrl}/games`;
        const [profileResponse, gamesResponse] = await Promise.all([
          fetch(profileUrl, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }),
          fetch(gamesUrl, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }),
        ]);

        if (!profileResponse.ok) {
          throw new Error(profileResponse.status === 404 ? "Player not found." : "Failed to load player profile.");
        }

        if (!gamesResponse.ok) {
          throw new Error("Failed to load player games.");
        }

        const [profileData, gamesData] = await Promise.all([
          profileResponse.json(),
          gamesResponse.json(),
        ]);

        if (!isActive) return;

        setUserData(profileData);
        setUserGames(Array.isArray(gamesData) ? gamesData : []);
      } catch (err) {
        if (!isActive) return;
        setError(err.message || "Failed to load player profile.");
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    loadPublicProfile();

    return () => {
      isActive = false;
    };
  }, [API_URL, playerId]);

  if (viewingOwnProfile) {
    return <Navigate to={ROUTES.PROFILE} replace />;
  }

  if (loading) {
    return (
      <>
        <Seo
          title="Player Profile | ZealFC"
          description="View a ZealFC player's stats and game history."
          robots="noindex, nofollow"
          canonicalPath={canonicalPath}
        />
        <div className="profile-container">
          <LoadingOverlay label="Loading player profile" />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Seo
          title="Player Profile | ZealFC"
          description="View a ZealFC player's stats and game history."
          robots="noindex, nofollow"
          canonicalPath={canonicalPath}
        />
        <div className="profile-container">
          <div>Error: {error}</div>
        </div>
      </>
    );
  }

  if (!userData) {
    return (
      <>
        <Seo
          title="Player Profile | ZealFC"
          description="View a ZealFC player's stats and game history."
          robots="noindex, nofollow"
          canonicalPath={canonicalPath}
        />
        <div className="profile-container">
          <div>Player not found.</div>
        </div>
      </>
    );
  }

  return (
    <>
      <Seo
        title={`${playerName} | ZealFC`}
        description={`View ${playerName}'s ZealFC stats and game history.`}
        robots="noindex, nofollow"
        canonicalPath={canonicalPath}
      />
      <div className="profile-container">
        <ProfileInfo
          userData={userData}
          readOnly
          hideSensitiveFields
        />
        <div className="profile-filter-wrap">
          <ProfileFilter
            games={userGames}
            user={userData}
            initialTab={initialTab}
            showSubscription={false}
            showUpcomingGames={false}
            statsProps={{
              userProfileOverride: userData,
              userGamesOverride: userGames,
              leaderboardCurrentUserId: userData._id,
              leaderboardViewerUserId: currentUser?._id || null,
            }}
          />
        </div>
      </div>
    </>
  );
}

export default PublicProfile;
